import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './AudioVisualizer.scss';
import { toRGBAWithAlpha } from '@utils';

const variants = {
  BARS: 'bars',
  WAVEFORM: 'waveform',
  DOTS: 'dots',
  RADIAL: 'radial',
  AREA: 'area',
  SPIRAL: 'spiral',
  MIRRORED_WAVEFORM: 'mirrored_waveform',
};

const IdleAudioVisualizer = ({
  variant = variants.RADIAL,
  theme = 'light',
  shadowColor,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = '#999';
    ctx.fillStyle = '#999';
    ctx.lineWidth = 1;
    ctx.shadowColor = `0px 4.393px 29.649px 5.491px ${shadowColor ? toRGBAWithAlpha(shadowColor) : 'rgba(110, 63, 243, 0.20)'}`;
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;

    function drawPlaceholder() {
      const midY = height / 2;

      switch (variant) {
        case variants.BARS: {
          const bars = 40;
          const barWidth = width / bars;
          for (let i = 0; i < bars; i++) {
            const barHeight = height * (0.2 + 0.3 * Math.sin(i * 0.3));
            ctx.fillRect(
              i * barWidth,
              height - barHeight,
              barWidth * 0.6,
              barHeight
            );
          }
          break;
        }
        case variants.WAVEFORM:
        case variants.MIRRORED_WAVEFORM: {
          ctx.beginPath();
          for (let x = 0; x < width; x++) {
            const y = midY + Math.sin(x * 0.05) * 20;
            ctx.lineTo(x, y);
          }

          if (variant === variants.MIRRORED_WAVEFORM) {
            ctx.beginPath();
            for (let x = 0; x < width; x++) {
              const y = midY - Math.sin(x * 0.05) * 20;
              ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
          break;
        }
        case variants.DOTS: {
          const cols = 30,
            rows = 10;
          const spacingX = width / cols;
          const spacingY = height / rows;
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const radius = 2 + Math.sin((r + c) * 0.5);
              ctx.beginPath();
              ctx.arc(
                c * spacingX + spacingX / 2,
                r * spacingY + spacingY / 2,
                radius,
                0,
                Math.PI * 2
              );
              ctx.fill();
            }
          }
          break;
        }
        case variants.RADIAL:
        case variants.SPIRAL: {
          const centerX = width / 2;
          const centerY = height / 2;
          const baseRadius = Math.min(width, height) / 3;
          const segments = 60;
          for (let i = 0; i < segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const radius = baseRadius + Math.sin(i * 0.3) * 10;
            const x1 = centerX + Math.cos(angle) * baseRadius;
            const y1 = centerY + Math.sin(angle) * baseRadius;
            const x2 = centerX + Math.cos(angle) * radius;
            const y2 = centerY + Math.sin(angle) * radius;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }
          break;
        }
        case variants.AREA: {
          ctx.beginPath();
          ctx.moveTo(0, midY);
          for (let x = 0; x < width; x++) {
            const y = midY + Math.sin(x * 0.05) * 20;
            ctx.lineTo(x, y);
          }
          ctx.lineTo(width, midY);
          ctx.lineTo(0, midY);
          ctx.closePath();
          ctx.fill();
          break;
        }
        default:
          console.warn(`Unknown variant: ${variant}`);
          return;
      }

      ctx.globalAlpha = 1;
    }

    drawPlaceholder();
  }, [shadowColor, variant]);

  return (
    <div className={`audio-visualizer ${theme} ${variant}`}>
      <canvas ref={canvasRef} className={variant} />
    </div>
  );
};

IdleAudioVisualizer.displayName = 'IdleAudioVisualizer';

IdleAudioVisualizer.propTypes = {
  variant: PropTypes.oneOf(Object.values(variants)),
  theme: PropTypes.oneOf(['light', 'dark']),
  shadowColor: PropTypes.string,
};

export default IdleAudioVisualizer;
export { IdleAudioVisualizer };
